import React from 'react'
import {InView} from 'react-intersection-observer'
import styled, {css} from 'styled-components'

import mq from '@/utils/media-query'

// Number of items to show on mobile
const MOBILE_ITEMS_COUNT = 6

interface MasonryLayoutProps {
    children: React.ReactNode
}

export const MasonryLayout = ({children}: MasonryLayoutProps) => {
    const items = React.Children.toArray(children)

    return (
        <Wrapper $useGridLayout={items.length < 5}>
            {items.map((child, index) => (
                <InView key={`item-${index}`} threshold={0.5} triggerOnce>
                    {({inView, ref}) => (
                        <ItemContainer ref={ref} $isInView={inView}>
                            {child}
                        </ItemContainer>
                    )}
                </InView>
            ))}
        </Wrapper>
    )
}

const MOBILE_BREAKPOINT = 'sm'
const TABLET_BREAKPOINT = 'md'

const ItemContainer = styled.div<{$isInView: boolean}>`
    display: inline-block;
    width: 100%;
    margin-bottom: ${({theme}) => theme.spacings.s24};
    transform: ${({$isInView}) => ($isInView ? 'translateY(0)' : 'translateY(40px)')};
    opacity: ${({$isInView}) => ($isInView ? 1 : 0)};
    transition: all 0.6s cubic-bezier(0.17, 0.55, 0.55, 1);
    will-change: transform, opacity;

    ${mq.min(TABLET_BREAKPOINT)} {
        margin-bottom: ${({theme}) => theme.spacings.s40};
    }
`

// Negative margin to counteract the margin-bottom on the items
const Wrapper = styled.div<{$useGridLayout: boolean}>`
    margin-bottom: -${({theme}) => theme.spacings.s24};
    column-count: 1;

    ${mq.min(MOBILE_BREAKPOINT)} {
        column-count: 3;
        column-gap: ${({theme}) => theme.spacings.s24};

        ${({$useGridLayout}) => $useGridLayout && gridLayout}
    }

    ${mq.min(TABLET_BREAKPOINT)} {
        column-gap: ${({theme}) => theme.spacings.s40};
        margin-bottom: -${({theme}) => theme.spacings.s40};
    }

    ${mq.max(MOBILE_BREAKPOINT)} {
        & > *:nth-child(n + ${MOBILE_ITEMS_COUNT + 1}) {
            display: none;
        }
    }
`

const gridLayout = css`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
`
